import { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import CasinoCard from "../global/CasinoCard";
import { RegionContext } from "../../../context/RegionContext";
import {
  getAllCasinos,
  getAllCasinoContentListings,
} from "../../../controllers/homeController";
import BestCasinosForBeginners from "../../../components/client-side/casinos/BestCasinosForBeginners";
import CasinosWithLargestGames from "../../../components/client-side/casinos/CasinosWithLargestGames";
import OtherHonorableMentions from "../../../components/client-side/casinos/OtherHonorableMentions";
import LowestMinimumDeposit from "../../../components/client-side/casinos/LowestMinimumDeposit";

const CasinosListing = () => {
  const navigate = useNavigate();
  const { selectedRegionId } = useContext(RegionContext);

  // UI States
  const [openIndex, setOpenIndex] = useState(0);
  const [casinos, setCasinos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);

  const [bonusType, setBonusType] = useState(null);
  const [sortBy, setSortBy] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1); // eslint-disable-next-line
  const [total, setTotal] = useState(0);

  // Content listings (static)
  const [casinoContentListing, setCasinoContentListing] = useState({});

  // Load static content listings once
  useEffect(() => {
    (async () => {
      try {
        const data = await getAllCasinoContentListings(selectedRegionId);
        setCasinoContentListing(data);
      } catch (err) {
        console.error("Error loading casino content listings:", err);
      }
    })();
  }, [selectedRegionId]);

  // Main data fetch — triggered by region OR any filter change
  const fetchCasinos = useCallback(
    async (pageNum = 1, reset = true) => {
      const isInitial = pageNum === 1 && reset;
      if (isInitial) setLoading(true);
      else setLoadMoreLoading(true);

      try {
        const data = await getAllCasinos({
          regionId: selectedRegionId,
          page: pageNum,
          limit: 10,
          bonusType: bonusType === "all" ? undefined : bonusType,
          sortBy: sortBy,
        });

        // Get the casinos from backend (already sorted)
        const incoming = data.casinos || [];

        if (reset) {
          setCasinos(incoming);
          setPage(1);
        } else {
          // When loading more, simply append to existing list
          setCasinos((prev) => [...prev, ...incoming]);
        }

        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
        setPage(pageNum);
      } catch (err) {
        console.error("Error fetching casinos:", err);
        if (reset) setCasinos([]);
      } finally {
        setLoading(false);
        setLoadMoreLoading(false);
      }
    },

    [selectedRegionId, bonusType, sortBy]
  );

  // Initial load + region change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchCasinos(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegionId]);

  // Refetch on any filter change (reset to page 1)
  useEffect(() => {
    fetchCasinos(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bonusType, sortBy, fetchCasinos]);

  // Load more
  const handleLoadMore = () => {
    if (page < totalPages && !loadMoreLoading) {
      fetchCasinos(page + 1, false);
    }
  };

  const handleToggle = useCallback(
    (index) => setOpenIndex(openIndex === index ? null : index),
    [openIndex]
  );

  const handleDetailsClick = (casino) => {
    navigate("/casino-details", { state: { casino } });
  };
  const mapToCardModel = (c) => ({
    id: c.id,
    Name: c.name,
    ImageUrl: c.imageUrl,
    Rating: c.rating,
    BestFor: c.bestFor,
    PayoutSpeed: c.payoutSpeed,
    Rtp: c.rtp,
    WelcomeOffer: c.welcomeOffer,
    MinDeposit: c.minDeposit,
    CasinoUrl: c.casinoUrl,
    PaymentMethods: c.paymentMethods || [],
    promoCode: c.promoCode || "",
    freeSpins: c.freeSpins ?? null,
    description: c.description || null,
  });

  return (
    <>
      {/* ===== HERO DISCLOSURE BAR ===== */}
      <motion.div
        className="sticky top-0 z-30 bg-background/95 dark:bg-gray-800 backdrop-blur-sm transition-colors duration-300"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="w-full max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-6 xl:px-20 flex flex-col md:flex-row items-center justify-between gap-3 py-3">
          <div className="flex items-center text-sm text-black dark:text-textDark/80"></div>

          <div className="flex flex-wrap items-center gap-4 text-black dark:text-white">
            {/* ===== BONUS TYPE DROPDOWN ===== */}
            <div className="flex items-center gap-2">
              <label htmlFor="bonus-type" className="text-sm font-medium">
                Bonus Type:
              </label>
              <div className="relative">
                <select
                  id="bonus-type"
                  value={bonusType ?? "all"}
                  onChange={(e) =>
                    setBonusType(
                      e.target.value === "all" ? null : e.target.value
                    )
                  }
                  className="border border-border dark:border-none rounded-lg bg-surface bg-white dark:bg-[#1a1a1a] text-sm px-3 py-1.5 pr-7 focus:outline-none focus:ring-2 focus:ring-primary/40 appearance-none"
                >
                  <option value="all">All Bonuses</option>
                  <option value="free_spins">Free Spins</option>
                  <option value="no_deposit">No Deposit</option>
                </select>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-300 pointer-events-none text-[12px]">
                  ▼
                </span>
              </div>
            </div>

            {/* ===== SORT BY DROPDOWN ===== */}
            <div className="flex items-center gap-2 ">
              <label htmlFor="sort-by" className="text-sm font-medium">
                Sort by:
              </label>
              <div className="relative">
                <select
                  id="sort-by"
                  value={sortBy ?? "all"}
                  onChange={(e) =>
                    setSortBy(e.target.value === "all" ? null : e.target.value)
                  }
                  className="border border-border dark:border-none rounded-lg bg-surface  bg-white dark:bg-[#1a1a1a] text-sm px-3 py-1.5 pr-7 focus:outline-none focus:ring-2 focus:ring-primary/40 appearance-none"
                >
                  <option value="all">All</option>
                  <option value="newest">Newest</option>
                  <option value="rating">Highest Rated</option>
                </select>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-300 pointer-events-none text-[12px]">
                  ▼
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ===== CASINO LIST ===== */}
      <section className="bg-background dark:bg-gray-800 text-text !py-4 dark:text-textDark pt-0 pb-12 transition-colors duration-300">
        <div className="w-full max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-6 xl:px-20 mt-6">
          {loading ? (
            <div className="space-y-4">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-32 rounded-xl border border-border dark:border-none bg-gray-200 dark:bg-gray-800 animate-pulse"
                />
              ))}
            </div>
          ) : casinos.length === 0 ? (
            <p className="text-center text-muted dark:text-mutedDark mt-10">
              No casinos found for the selected filters.
            </p>
          ) : (
            <>
              <div className="space-y-6">
                {casinos.map((casino, index) => (
                  <motion.div
                    key={casino.id}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.35, delay: index * 0.06 }}
                  >
                    <CasinoCard
                      casino={mapToCardModel(casino)}
                      index={index}
                      isOpen={openIndex === index}
                      onToggle={handleToggle}
                      onDetailsClick={handleDetailsClick}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Load More */}
              {page < totalPages && (
                <div className="flex justify-center mt-8">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadMoreLoading}
                    className="px-6 py-2 rounded border dark:border-none bg-primary text-white font-semibold disabled:opacity-50"
                  >
                    {loadMoreLoading ? "Loading..." : "Load More"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ===== INFO SECTION & FEATURED BLOCKS ===== */}
      <motion.section
        className="bg-background dark:bg-darkbg text-text dark:text-textDark py-12 transition-colors duration-300"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="w-full text-black dark:text-white max-w-[900px] mx-auto px-6 text-left">
          <h2 className="text-xl sm:text-2xl font-bold leading-snug mb-3">
            As the online gaming industry flourishes, players are becoming more
            discerning...
          </h2>
          <p className="text-base text-text/80 dark:text-textDark/80 mb-6">
            Each casino on our list has been vetted for fairness and security,
            ensuring a safe gaming environment.
          </p>
          {/* ... rest of your text ... */}
        </div>
      </motion.section>
      {casinoContentListing?.forBeginners?.length > 0 && (
        <BestCasinosForBeginners data={casinoContentListing.forBeginners[0]} />
      )}
      {casinoContentListing?.largestGames?.length > 0 && (
        <CasinosWithLargestGames data={casinoContentListing.largestGames[0]} />
      )}
      {casinoContentListing?.honorable?.length > 0 && (
        <OtherHonorableMentions data={casinoContentListing.honorable} />
      )}
      {casinoContentListing?.lowestDeposit?.length > 0 && (
        <LowestMinimumDeposit data={casinoContentListing.lowestDeposit} />
      )}
    </>
  );
};

export default CasinosListing;
